/**
 * DV-AUTH-002: Cookie Echo Panel
 * Validates cookies in cross-site contexts via /api/auth/_echo
 */

import React from 'react';
import { RefreshCw, Cookie, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';

export interface CookieEchoResult {
  receivedCookies: Record<string, string>;
  hasAt: boolean;
  hasRt: boolean;
  origin?: string;
  userAgent?: string;
  timestamp: string;
}

export interface CookieEchoPanelProps {
  echoResult: CookieEchoResult | null;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onGetRemediationHints: (missing: string[]) => string[];
}

export const CookieEchoPanel: React.FC<CookieEchoPanelProps> = ({
  echoResult,
  isLoading,
  error,
  onRefresh,
  onGetRemediationHints
}) => {
  const missingCookies = echoResult ?
    ['hisi_at', 'hisi_rt'].filter(cookie => !echoResult.receivedCookies[cookie]) :
    [];

  const remediationHints = missingCookies.length > 0 ?
    onGetRemediationHints(missingCookies) :
    [];

  // Helper to mask sensitive cookie values (show first/last 4 chars)
  const maskCookieValue = (name: string, value: string): string => {
    const isAuthCookie = ['hisi_at', 'hisi_rt'].includes(name);
    if (!isAuthCookie || value.length <= 8) {
      return value; // Don't mask non-auth cookies or short values
    }
    return `${value.slice(0, 4)}...${value.slice(-4)}`;
  };

  return (
    <div
      className="mt-4 rounded-lg border"
      style={{
        backgroundColor: 'var(--bg-elevated)',
        borderColor: 'var(--border-default)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 border-b"
        style={{
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="flex items-center space-x-2">
          <Cookie className="w-4 h-4" style={{ color: 'var(--accent)' }} />
          <span
            className="text-sm font-medium"
            style={{ color: 'var(--fg-primary)' }}
          >
            Cookie Validation
          </span>
          {echoResult && (
            <div className="flex items-center space-x-1">
              <div
                className="px-1.5 py-0.5 rounded text-xs"
                style={{
                  backgroundColor: echoResult.hasAt ? 'var(--bg-success)' : 'var(--bg-error)',
                  color: echoResult.hasAt ? 'var(--fg-success)' : 'var(--fg-error)',
                }}
              >
                AT
              </div>
              <div
                className="px-1.5 py-0.5 rounded text-xs"
                style={{
                  backgroundColor: echoResult.hasRt ? 'var(--bg-success)' : 'var(--bg-error)',
                  color: echoResult.hasRt ? 'var(--fg-success)' : 'var(--fg-error)',
                }}
              >
                RT
              </div>
            </div>
          )}
        </div>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="flex items-center space-x-1 px-2 py-1 text-xs rounded border transition-colors"
          style={{
            backgroundColor: isLoading ? 'var(--bg-surface)' : 'var(--bg-interactive-hover)',
            borderColor: 'var(--border-default)',
            color: isLoading ? 'var(--fg-muted)' : 'var(--fg-primary)',
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
          data-testid="cookie-echo-test"
        >
          <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
          <span>{isLoading ? 'Testing...' : 'Test'}</span>
        </button>
      </div>

      {/* Content */}
      <div className="p-3 space-y-3">
        {error && (
          <div
            className="p-2 rounded border text-sm"
            style={{
              backgroundColor: 'var(--bg-error)',
              borderColor: 'var(--border-error)',
              color: 'var(--fg-error)',
            }}
          >
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {isLoading && (
          <div
            className="text-center py-4 text-sm"
            style={{ color: 'var(--fg-muted)' }}
          >
            Testing cookie validation...
          </div>
        )}

        {echoResult && (
          <div className="space-y-3">
            {/* Cookie Status Summary */}
            <div className="grid grid-cols-2 gap-2">
              <div
                className="p-2 rounded border text-center"
                style={{
                  backgroundColor: echoResult.hasAt ? 'var(--bg-success)' : 'var(--bg-error)',
                  borderColor: echoResult.hasAt ? 'var(--border-success)' : 'var(--border-error)',
                }}
              >
                <div className="flex items-center justify-center space-x-1">
                  {echoResult.hasAt ? (
                    <CheckCircle className="w-3 h-3" style={{ color: 'var(--fg-success)' }} />
                  ) : (
                    <AlertTriangle className="w-3 h-3" style={{ color: 'var(--fg-error)' }} />
                  )}
                  <span
                    className="text-xs font-medium"
                    style={{
                      color: echoResult.hasAt ? 'var(--fg-success)' : 'var(--fg-error)',
                    }}
                  >
                    Access Token
                  </span>
                </div>
                <div
                  className="text-xs mt-0.5"
                  style={{
                    color: echoResult.hasAt ? 'var(--fg-success)' : 'var(--fg-error)',
                  }}
                  data-testid="cookie-echo-status-at"
                >
                  {echoResult.hasAt ? 'Present' : 'Missing'}
                </div>
              </div>

              <div
                className="p-2 rounded border text-center"
                style={{
                  backgroundColor: echoResult.hasRt ? 'var(--bg-success)' : 'var(--bg-error)',
                  borderColor: echoResult.hasRt ? 'var(--border-success)' : 'var(--border-error)',
                }}
              >
                <div className="flex items-center justify-center space-x-1">
                  {echoResult.hasRt ? (
                    <CheckCircle className="w-3 h-3" style={{ color: 'var(--fg-success)' }} />
                  ) : (
                    <AlertTriangle className="w-3 h-3" style={{ color: 'var(--fg-error)' }} />
                  )}
                  <span
                    className="text-xs font-medium"
                    style={{
                      color: echoResult.hasRt ? 'var(--fg-success)' : 'var(--fg-error)',
                    }}
                  >
                    Refresh Token
                  </span>
                </div>
                <div
                  className="text-xs mt-0.5"
                  style={{
                    color: echoResult.hasRt ? 'var(--fg-success)' : 'var(--fg-error)',
                  }}
                  data-testid="cookie-echo-status-rt"
                >
                  {echoResult.hasRt ? 'Present' : 'Missing'}
                </div>
              </div>
            </div>

            {/* Detailed Cookie List */}
            <div className="space-y-1">
              <div
                className="text-xs font-medium"
                style={{ color: 'var(--fg-secondary)' }}
              >
                Received Cookies ({Object.keys(echoResult.receivedCookies).length}):
              </div>
              {Object.keys(echoResult.receivedCookies).length === 0 ? (
                <div
                  className="text-xs italic"
                  style={{ color: 'var(--fg-muted)' }}
                >
                  No cookies received by server
                </div>
              ) : (
                <div className="space-y-1">
                  {Object.entries(echoResult.receivedCookies).map(([name, value]) => (
                    <div
                      key={name}
                      className="flex items-center justify-between p-1.5 rounded text-xs border"
                      style={{
                        backgroundColor: ['hisi_at', 'hisi_rt'].includes(name) ?
                          'var(--bg-surface)' : 'var(--bg-elevated)',
                        borderColor: 'var(--border-subtle)',
                      }}
                    >
                      <span
                        className="font-mono font-medium"
                        style={{
                          color: ['hisi_at', 'hisi_rt'].includes(name) ?
                            'var(--accent)' : 'var(--fg-secondary)',
                        }}
                      >
                        {name}
                      </span>
                      <span
                        className="font-mono truncate max-w-[8rem]"
                        style={{ color: 'var(--fg-muted)' }}
                        title={value}
                      >
                        {maskCookieValue(name, value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Remediation Hints */}
            {(remediationHints.length > 0 || (echoResult && Object.keys(echoResult.receivedCookies).length === 0)) && (
              <div
                className="p-2 rounded border"
                style={{
                  backgroundColor: 'var(--bg-warning)',
                  borderColor: 'var(--border-warning)',
                }}
              >
                <div className="flex items-start space-x-2">
                  <AlertTriangle
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ color: 'var(--fg-warning)' }}
                  />
                  <div>
                    <div
                      className="text-xs font-medium mb-1"
                      style={{ color: 'var(--fg-warning)' }}
                    >
                      Cookie Issues Detected
                    </div>
                    <div className="space-y-1">
                      {echoResult && Object.keys(echoResult.receivedCookies).length === 0 && (
                        <>
                          <div
                            className="text-xs font-medium"
                            style={{ color: 'var(--accent-error)' }}
                          >
                            🚫 Zero cookies received - critical issue detected
                          </div>
                          {echoResult.origin && echoResult.origin !== window.location.origin ? (
                            <>
                              <div
                                className="text-xs"
                                style={{ color: 'var(--fg-warning)' }}
                              >
                                • Cross-site context: Third-party cookies likely blocked
                              </div>
                              <div
                                className="text-xs"
                                style={{ color: 'var(--fg-warning)' }}
                              >
                                • Enable "Allow third-party cookies" in browser settings
                              </div>
                              <div
                                className="text-xs"
                                style={{ color: 'var(--fg-warning)' }}
                              >
                                • Server must use SameSite=None;Secure;domain attributes
                              </div>
                            </>
                          ) : (
                            <div
                              className="text-xs"
                              style={{ color: 'var(--fg-warning)' }}
                            >
                              • Test in api.hi-si.ai context and verify Set-Cookie attributes
                            </div>
                          )}
                        </>
                      )}
                      {remediationHints.map((hint, index) => (
                        <div
                          key={index}
                          className="text-xs"
                          style={{ color: 'var(--fg-warning)' }}
                        >
                          • {hint}
                        </div>
                      ))}
                    </div>
                    <div className="mt-2">
                      <a
                        href="https://docs.hi-si.ai/auth/pkce-hardening"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1 text-xs underline hover:no-underline"
                        style={{ color: 'var(--accent)' }}
                      >
                        <ExternalLink className="w-3 h-3" />
                        <span>PKCE/Cookie Hardening Guide</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Metadata */}
            {echoResult.origin && (
              <div className="text-xs space-y-1">
                <div style={{ color: 'var(--fg-secondary)' }}>
                  <span className="font-medium">Origin:</span>{' '}
                  <span style={{ color: 'var(--fg-muted)' }}>{echoResult.origin}</span>
                </div>
                <div style={{ color: 'var(--fg-secondary)' }}>
                  <span className="font-medium">Tested:</span>{' '}
                  <span style={{ color: 'var(--fg-muted)' }}>
                    {new Date(echoResult.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {!echoResult && !isLoading && !error && (
          <div
            className="text-center py-4 text-sm"
            style={{ color: 'var(--fg-muted)' }}
          >
            Click "Test" to validate cookies in cross-site context
          </div>
        )}
      </div>
    </div>
  );
};